/* Arrows and dots for a rail of cards. The rail itself is a native horizontal
   scroller, so swipe, trackpad and keyboard scrolling already work without
   this; all it adds is the controls, and it hides them whenever every card
   already fits on screen.

   Section-agnostic on purpose — the reviews rail and the steps rail both use
   it — so it finds its parts by data attribute rather than by class name:

     <pdp-carousel>
       <div data-carousel-rail>
         <div data-carousel-slide>…</div>
       </div>
       {% render 'pdp-carousel-nav', … %}
     </pdp-carousel> */
(function () {
  if (customElements.get('pdp-carousel')) return;

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  class Carousel extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-carousel-rail]');
      this.nav = this.querySelector('.pdp-carousel__nav');
      if (!this.rail || !this.nav) return;

      this.cards = Array.from(this.rail.querySelectorAll('[data-carousel-slide]'));
      this.dots = Array.from(this.nav.querySelectorAll('.pdp-carousel__dot'));
      this.arrows = Array.from(this.nav.querySelectorAll('.pdp-carousel__arrow'));
      if (this.cards.length < 2) return;

      this.index = 0;

      this.arrows.forEach((arrow) => {
        arrow.addEventListener('click', () => {
          this.go(this.index + Number(arrow.dataset.dir));
        });
      });
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.go(i));
      });

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.sync();
        });
      };
      this.rail.addEventListener('scroll', this.onScroll, { passive: true });

      // The nav is only useful once there is something off screen to reach, and
      // how many cards fit changes with the viewport — the steps rail turns
      // back into a plain grid past 750px, at which point it goes away.
      this.onResize = () => this.sync();
      window.addEventListener('resize', this.onResize);

      // Picking a card in the theme editor should bring it into view rather
      // than leaving the merchant looking at one they did not select.
      this.addEventListener('shopify:block:select', (event) => {
        const card = event.target.closest('[data-carousel-slide]');
        const index = this.cards.indexOf(card);
        if (index >= 0) this.go(index);
      });

      this.toggleAttribute('data-ready', true);
      this.sync();
    }

    disconnectedCallback() {
      if (this.rail) this.rail.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    /* Scrolling is asked for as a distance from where the card sits now, so the
       maths holds in both writing directions without touching scrollLeft. It
       also holds whichever edge the cards snap to: a snapped card's leading
       edge always lands on the rail's leading padding. */
    go(target) {
      const index = Math.max(0, Math.min(this.cards.length - 1, target));
      const card = this.cards[index];
      if (!card) return;
      this.rail.scrollBy({
        left: card.getBoundingClientRect().left - this.railStart(),
        behavior: REDUCED.matches ? 'auto' : 'smooth',
      });
    }

    railStart() {
      const pad = parseFloat(getComputedStyle(this.rail).paddingInlineStart) || 0;
      return this.rail.getBoundingClientRect().left + pad;
    }

    sync() {
      const overflows = this.rail.scrollWidth - this.rail.clientWidth > 1;
      this.nav.hidden = !overflows;
      if (!overflows) return;

      const start = this.railStart();
      let index = 0;
      let closest = Infinity;
      this.cards.forEach((card, i) => {
        const distance = Math.abs(card.getBoundingClientRect().left - start);
        if (distance < closest) {
          closest = distance;
          index = i;
        }
      });

      const atStart = this.rail.scrollLeft <= 1;
      const atEnd =
        Math.abs(this.rail.scrollLeft) >=
        this.rail.scrollWidth - this.rail.clientWidth - 1;
      // The last cards share the final scroll position once more than one fits,
      // so the end of the rail always lights the last dot.
      if (atEnd) index = this.cards.length - 1;

      this.index = index;
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      this.arrows.forEach((arrow) => {
        arrow.disabled = Number(arrow.dataset.dir) < 0 ? atStart : atEnd;
      });
    }
  }

  customElements.define('pdp-carousel', Carousel);
})();
